import { FunctionComponent, useEffect, useState } from 'react';
import { useBooking } from '../bookings/bookingService';
import { BookingDetails } from '../bookings/models/BookingDetails';
import { BookingStatus } from '../bookings/models/BookingStatus';
import AlertModal from '../modal/AlertModal';
import CancellationView from './views/CancellationView';
import DetailsView from './views/DetailsView';
import MessageView from './views/MessageView';

type Props = {
    bookingId: string;
    hasMultipleObjects: boolean;
    onClose: () => void;
    onRefresh: () => void;
};

enum ModalView {
    CANCELLATION,
    DETAILS,
    MESSAGE,
}

const BookingModal: FunctionComponent<Props> = ({ bookingId, hasMultipleObjects, onClose, onRefresh }) => {
    const { booking, error } = useBooking(bookingId);
    const [isArchived, setIsArchived] = useState<boolean>(false);
    const [activeView, setActiveView] = useState<ModalView>();

    useEffect(() => {
        if (booking) {
            setIsArchived(getArchiveStatus(booking));
            setActiveView(ModalView.DETAILS);
        }
    }, [booking]);

    if (error) {
        return <AlertModal heading="Ett fel har uppstått" text="Kunde inte hitta den efterfrågade bokningen." onClose={onClose} visible={true} />;
    }

    if (!booking) {
        return null;
    }

    const showCancellationView = isArchived ? undefined : () => setActiveView(ModalView.CANCELLATION);
    const showMessageView = booking.isInternalBooking || booking.isIntermediateBooking ? undefined : () => setActiveView(ModalView.MESSAGE);

    switch (activeView) {
        case ModalView.CANCELLATION:
            return <CancellationView booking={booking} onClose={onClose} onRefresh={onRefresh} />;
        case ModalView.DETAILS:
            return (
                <DetailsView
                    booking={booking}
                    hasMultipleObjects={hasMultipleObjects}
                    isArchived={isArchived}
                    onClose={onClose}
                    showCancellationView={showCancellationView}
                    showMessageView={showMessageView}
                />
            );
        case ModalView.MESSAGE:
            return <MessageView booking={booking} onClose={onClose} />;
        default:
            return null;
    }
};

const getArchiveStatus = (booking: BookingDetails): boolean => {
    if (booking.status !== BookingStatus.Booked) {
        return true;
    }

    if (new Date(booking.endTime) < new Date()) {
        return true;
    }

    return false;
};

export default BookingModal;
